import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect } from "react";
import { Box, Stack, Dialog } from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { useParams, useNavigate } from "react-router";
import TopBar from "../../../../../../component/TopBar";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import FormField from "../../../../../../component/Formfield";
import SectionLoader from "../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../component/EmptyState";
import { getPayslipById, raisePayslipQuery } from "../../../../../../services/payroll";
import { getOrgDetails } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont, radius } from "../../../../../../theme";
import { money, periodLabel } from "../payrollUtils";
import { rupeesInWords } from "../numberToWords";

const Row = ({ label, amount, strong, danger }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: "0.5rem", borderBottom: `1px solid ${tokens.border}` }}>
    <Box sx={{ fontSize: "0.875rem", color: strong ? tokens.text : tokens.textMuted, fontWeight: strong ? 700 : 400 }}>{label}</Box>
    <Box sx={{ fontSize: "0.875rem", fontWeight: strong ? 700 : 500, color: danger ? tokens.danger : tokens.text }}>{money(amount)}</Box>
  </Stack>
);

const Panel = ({ title, lines, total, totalLabel, danger, accent }) => (
  <Box sx={{ flex: 1, background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem" }}>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "0.75rem", color: accent }}>{title}</Box>
    {(lines || []).length === 0 ? <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, py: "0.5rem" }}>None</Box>
      : (lines || []).map((l, i) => <Row key={i} label={l.name} amount={l.amount} danger={danger} />)}
    <Row label={totalLabel} amount={total} strong danger={danger} />
  </Box>
);

const PayslipDetail = ({ basePath = "/app/settings/payroll", allowQuery = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryOpen, setQueryOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [raising, setRaising] = useState(false);
  const org = getOrgDetails() || {};

  const raise = async () => {
    if (!subject.trim()) { notify.error("Add a subject"); return; }
    setRaising(true);
    try {
      const res = await raisePayslipQuery({ payslip: id, subject: subject.trim(), body: body.trim() });
      if (res?.success === false) throw apiError(res, "Could not raise query");
      notify.success("Concern raised — HR will respond");
      setQueryOpen(false); setSubject(""); setBody("");
    } catch (e) { notify.error(e?.message || "Could not raise query"); }
    finally { setRaising(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getPayslipById(id);
        if (res?.success === false) throw new Error(res?.message || "Failed to load payslip");
        if (active) setPayslip(res?.data || null);
      } catch (e) {
        notify.error(e?.message || "Failed to load payslip");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Payslip"} /><SectionLoader minHeight="50vh" /></Box>;
  if (!payslip) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Payslip"} /><EmptyState title="Payslip not found" /></Box>;

  const emp = payslip.employee;
  const name = emp ? `${emp.fname || ""} ${emp.lname || ""}`.trim() : "—";
  const employerTotal = (payslip.employerContributions || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const orgName = org.organizationName || org.name || "Company";

  return (
    <Box sx={{ width: "100%" }}>
      <Box className="payslip-noprint">
        <TopBar MainHeading={"Payslip"} breadcrumbs={[{ label: "Payroll", href: basePath }, { label: periodLabel(payslip.month, payslip.year) }]} />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: "1rem" }}>
          <BtnSecondary onClick={() => navigate(-1)} sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
          {allowQuery && (
            <BtnSecondary onClick={() => setQueryOpen(true)} sx={{ borderRadius: "8px" }}>
              <FlagOutlinedIcon sx={{ fontSize: "1.05rem", mr: 0.5 }} /> Raise a concern
            </BtnSecondary>
          )}
          <BtnPrimary onClick={() => window.print()} sx={{ borderRadius: "8px" }}>
            <PrintOutlinedIcon sx={{ fontSize: "1.1rem", mr: 0.5 }} /> Print / PDF
          </BtnPrimary>
        </Stack>
      </Box>

      <Box id="payslip-sheet" sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "2rem", maxWidth: "60rem", mx: "auto" }}>
        {/* company header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ pb: "1.25rem", borderBottom: `2px solid ${tokens.text}`, mb: "1.5rem" }}>
          <Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{orgName}</Box>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Payslip for {periodLabel(payslip.month, payslip.year)}</Box>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: payslip.status === "paid" ? tokens.successText : tokens.warningText, textTransform: "uppercase", letterSpacing: "0.05em" }}>{payslip.status}</Box>
          </Box>
        </Stack>

        {/* employee meta */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "1rem", mb: "1.5rem" }}>
          {[["Employee", name], ["Emp ID", emp?.aliasId || "—"], ["Designation", emp?.designation || "—"], ["Annual CTC", money(payslip.ctc)]].map(([k, v]) => (
            <Box key={k}>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>{k}</Box>
              <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mt: "0.25rem" }}>{v}</Box>
            </Box>
          ))}
        </Box>

        {/* earnings + deductions */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <Panel title="Earnings" lines={payslip.earnings} total={payslip.grossPay} totalLabel="Gross Earnings" accent={tokens.success} />
          <Panel title="Deductions" lines={payslip.deductions} total={payslip.totalDeductions} totalLabel="Total Deductions" danger accent={tokens.danger} />
        </Stack>

        {/* net pay banner */}
        <Box sx={{ mt: "1.5rem", background: tokens.successTint, borderRadius: "8px", p: "14px 16px" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase" }}>Net Pay</Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.text, fontStyle: "italic" }}>{rupeesInWords(payslip.netPay)}</Box>
            </Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 700, color: tokens.successText }}>{money(payslip.netPay)}</Box>
          </Stack>
        </Box>

        {/* employer contributions + YTD */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: "1.5rem" }}>
          {(payslip.employerContributions || []).length > 0 && (
            <Box sx={{ flex: 1, border: `1px solid ${tokens.border}`, borderRadius: "12px", p: "1.5rem" }}>
              <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "0.75rem" }}>Employer Contributions</Box>
              {payslip.employerContributions.map((e, i) => <Row key={i} label={e.name} amount={e.amount} />)}
              <Row label="Total (cost to company)" amount={employerTotal} strong />
            </Box>
          )}
          {payslip.ytd && (
            <Box sx={{ flex: 1, border: `1px solid ${tokens.border}`, borderRadius: "12px", p: "1.5rem" }}>
              <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "0.75rem" }}>Year to Date ({payslip.year})</Box>
              <Row label="Gross earnings" amount={payslip.ytd.gross} />
              <Row label="Deductions" amount={payslip.ytd.deductions} danger />
              <Row label="Net paid" amount={payslip.ytd.net} strong />
            </Box>
          )}
        </Stack>

        <Box sx={{ mt: "1.5rem", fontSize: "0.8125rem", color: tokens.textMuted, textAlign: "center" }}>
          This is a system-generated payslip and does not require a signature.
        </Box>
      </Box>

      <Dialog open={queryOpen} onClose={() => setQueryOpen(false)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "30rem", maxWidth: "94vw" } }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "0.25rem" }}>Raise a concern</Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>About your {periodLabel(payslip.month, payslip.year)} payslip. HR will respond here.</Box>
        <Box sx={{ mb: "1rem" }}>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Subject</Box>
          <FormField type="text" value={subject} onChange={(e) => setSubject(e.target.value)} fullwidth placeholder="e.g. HRA looks incorrect" />
        </Box>
        <Box>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Details</Box>
          <FormField type="text" value={body} onChange={(e) => setBody(e.target.value)} fullwidth row={3} />
        </Box>
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "2rem" }}>
          <BtnSecondary onClick={() => setQueryOpen(false)} disabled={raising} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={raise} disabled={raising} sx={{ borderRadius: "8px" }}>{raising ? "Submitting…" : "Submit"}</BtnPrimary>
        </Stack>
      </Dialog>

      <style>{`@media print {
        body * { visibility: hidden; }
        #payslip-sheet, #payslip-sheet * { visibility: visible; }
        #payslip-sheet { position: absolute; left: 0; top: 0; width: 100%; border: none; }
        .payslip-noprint { display: none !important; }
      }`}</style>
    </Box>
  );
};

export default PayslipDetail;
